import type { ComponentType } from "react";

/**
 * A page of an app; the menu places it in an area (app/areas.ts). to may carry a query when
 * the page is a view of a shared list (customers and suppliers). label is an i18n key; perm:
 * any of these; when: a condition of the workspace (see useConditions in app/apps.ts), for a
 * page that has nothing to offer until then, such as transfers with a single warehouse.
 */
export interface AppTab {
  to: string;
  label: string;
  perm?: string[];
  when?: "manyWarehouses";
}

/**
 * What an app brings to the shell. The key is its module name; its name and
 * description are the i18n keys apps.<key>.name and apps.<key>.description, and its
 * "start here" steps are labelled setup.<key>.<step>.
 */
export interface AppInfo {
  key: string;
  icon: ComponentType<{ className?: string }>;
  /** The app's sections; the first one the user may open is where the app opens. */
  tabs: AppTab[];
  /** "+ New" menu entries. */
  create?: { label: string; to: string; perm: string }[];
  /** The app's settings page. */
  settings?: { to: string; perm: string[] };
  /** Where each of the app's "start here" steps is done. */
  setup?: Record<string, string>;
  /** Shown while any of these modules is on, instead of being an app of its own. */
  shownWith?: string[];
}

/** The tab of path in app, if any: the longest tab path (without its query) that path is on or below. */
export function tabOf(app: AppInfo, path: string): AppTab | undefined {
  const base = (t: AppTab) => t.to.split("?")[0];
  return app.tabs
    .filter((t) => path === base(t) || path.startsWith(base(t) + "/"))
    .sort((a, b) => base(b).length - base(a).length)[0];
}
